import { readJson, readItem, writeItem, writeJson } from "./safeStorage";

/**
 * Где остановились в серии — на этом устройстве. В контракте нет маршрута, куда
 * сообщать позицию (listenedSec только читается), поэтому память — localStorage.
 */

export interface SavedPosition {
  positionSec: number;
  durationSec: number;
  finished: boolean;
  updatedAt: number;
}

/** Ближе 5 секунд к началу или к концу — начать сначала: это не «остановился», а «не начинал» или «дослушал». */
export const RESUME_MARGIN_SEC = 5;

function isPosition(value: unknown): value is SavedPosition {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.positionSec === "number" &&
    typeof v.durationSec === "number" &&
    typeof v.finished === "boolean" &&
    typeof v.updatedAt === "number"
  );
}

const key = (trackKey: string) => `scrile.pos.${trackKey}`;

export function loadPosition(trackKey: string): SavedPosition | null {
  return readJson("local", key(trackKey), isPosition);
}

export function savePosition(trackKey: string, position: SavedPosition): void {
  writeJson("local", key(trackKey), position);
}

/** С какой секунды продолжить дорожку длиной durationSec. */
export function resumePoint(saved: SavedPosition | null, durationSec: number): number {
  if (!saved || saved.finished) return 0;
  const duration = Number.isFinite(durationSec) && durationSec > 0 ? durationSec : saved.durationSec;
  if (saved.positionSec < RESUME_MARGIN_SEC) return 0;
  if (duration > 0 && saved.positionSec > duration - RESUME_MARGIN_SEC) return 0;
  return saved.positionSec;
}

/** Доля прослушанного для полоски в списке: 0..1. */
export function progressShare(saved: SavedPosition | null): number {
  if (!saved) return 0;
  if (saved.finished) return 1;
  if (saved.durationSec <= 0) return 0;
  return Math.min(1, Math.max(0, saved.positionSec / saved.durationSec));
}

export function episodeKey(heroSlug: string, ordinal: number): string {
  return `${heroSlug}:ep:${ordinal}`;
}

export function extraKey(heroSlug: string, id: string): string {
  return `${heroSlug}:extra:${id}`;
}

// Скорость — общая настройка человека, а не серии
const RATE_KEY = "scrile.rate";
export const RATES = [1, 1.25, 1.5, 2, 0.75] as const;

export function loadRate(): number {
  const value = Number(readItem("local", RATE_KEY));
  return (RATES as readonly number[]).includes(value) ? value : 1;
}

export function saveRate(rate: number): void {
  writeItem("local", RATE_KEY, String(rate));
}

export function nextRate(rate: number): number {
  const index = (RATES as readonly number[]).indexOf(rate);
  return RATES[(index + 1) % RATES.length] ?? 1;
}
